import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import { lighten } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import { Create, Delete } from "@mui/icons-material";
import { ownerValues } from "./helper";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort } = props;
  const headCells = [
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: useIntl().formatMessage({ id: "widget.formInputName" }),
    },
    {
      id: "description",
      numeric: false,
      disablePadding: false,
      label: useIntl().formatMessage({ id: "widget.formInputDesc" }),
    },
    {
      id: "owner",
      numeric: false,
      disablePadding: false,
      label: useIntl().formatMessage({
        id: "widget.dashboardTableHeadCellOwner",
      }),
    },
    {
      id: "template",
      numeric: false,
      disablePadding: false,
      label: useIntl().formatMessage({
        id: "widget.dashboardTableHeadCellTemplate",
      }),
    },
    {
      id: "background",
      numeric: false,
      disablePadding: false,
      label: useIntl().formatMessage({ id: "widget.dashboardColor" }),
    },
    { id: "", label: "", numeric: false, disablePadding: false },
  ];

  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding="default"
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const useToolbarStyles = makeStyles(theme => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    display: "block",
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: "1 1 100%",
  },
  tableHeading: {
    marginBottom: 20,
  },
  addButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: "4px 12px",
    fontSize: 10,
    "&:hover": {
      backgroundColor: lighten(theme.palette.primary.main, 0.25),
      color: theme.palette.common.white,
    },
  },
  buttonWrapper: {
    marginBottom: 12,
  },
}));

const EnhancedTableToolbar = ({ onAdd }) => {
  const classes = useToolbarStyles();

  return (
    <Toolbar className={classes.root}>
      <Box className={classes.tableHeading}>
        <Typography
          className={classes.title}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          <FormattedMessage id="widget.manageDashboards" />
        </Typography>
      </Box>
      <Box className={classes.buttonWrapper}>
        <Button className={classes.addButton} size="small" onClick={onAdd}>
          + <FormattedMessage id="widget.addDashboard" />
        </Button>
      </Box>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  onAdd: PropTypes.func.isRequired,
};

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  iconEdit: {
    margin: theme.spacing(1),
    marginLeft: "auto",
  },
  actionCell: {
    display: "flex",
    alignItems: "center",
  },
  colorBox: {
    width: 20,
    height: 20,
    borderRadius: 4,
  },
}));

const ManageDashboards = ({ onAdd, onDelete, onEdit, rows }) => {
  const classes = useStyles();
  const intl = useIntl();
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const renderToLabel = ({ from, to, count }) => {
    return `${from}-${to} ${intl.formatMessage({
      id: "pagination.of",
    })} ${count}`;
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = event => {
    setDense(event.target.checked);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <EnhancedTableToolbar onAdd={onAdd} />
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow hover tabIndex={-1} key={row.name}>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="default"
                      >
                        {row.name}
                      </TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>
                        <FormattedMessage id={`${ownerValues[row.owner]}`} />
                      </TableCell>
                      <TableCell>{row.template}</TableCell>
                      <TableCell>
                        <Box className={classes.actionCell}>
                          <Box
                            className={classes.colorBox}
                            style={{ backgroundColor: row.background }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box className={classes.actionCell}>
                          <IconButton
                            className={classes.iconEdit}
                            size="small"
                            onClick={() => onEdit(row, index)}
                          >
                            <Create fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            className={classes.iconEdit}
                            size="small"
                            onClick={() => onDelete(index)}
                          >
                            <Delete fontSize="inherit" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage={useIntl().formatMessage({
            id: "context.showRows",
          })}
          labelDisplayedRows={(f, t, c) => renderToLabel(f, t, c)}
        />
      </Paper>
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label={useIntl().formatMessage({ id: "widget.dashboardTableDense" })}
      />
    </div>
  );
};

ManageDashboards.defaultProps = {
  rows: [],
};

ManageDashboards.propTypes = {
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  rows: PropTypes.instanceOf(Array),
};

export default ManageDashboards;
